import { Controller } from '@hotwired/stimulus';
import { confirmMessage } from './message_controller';
import { canOpenViaShortcut, pageIsCovered } from '../modal_shortcuts';

export default class extends Controller {
  static targets = ['list', 'item', 'search'];

  /* ────────── lifecycle ────────── */

  connect() {
    this._openKey = this.openKey.bind(this);
    this._shortcutKey = this.shortcutKey.bind(this);
    this._escapeKey = this.escapeKey.bind(this);

    document.addEventListener('keydown', this._openKey);
    document.addEventListener('keydown', this._shortcutKey);
    document.addEventListener('keydown', this._escapeKey);
  }

  disconnect() {
    document.removeEventListener('keydown', this._openKey);
    document.removeEventListener('keydown', this._shortcutKey);
    document.removeEventListener('keydown', this._escapeKey);
  }

  /* ────────── global keys ────────── */

  openKey(e) {
    if (e.key === 'a' && canOpenViaShortcut(e)) {
      e.preventDefault();
      this.open();
    }
  }

  shortcutKey(e) {
    if (!e.ctrlKey || !e.altKey || e.metaKey || e.shiftKey) return;
    if (pageIsCovered()) return;

    const key = e.key.toLowerCase();
    const row = this.itemTargets.find((el) => el.dataset.actionModalShortcutValue === key);

    if (row) {
      e.preventDefault();
      row.click();
    }
  }

  escapeKey(e) {
    if (e.key === 'Escape' && !this.element.hidden) {
      e.preventDefault();
      this.close();
    }
  }

  /* ────────── modal visibility ────────── */

  open() {
    this.element.hidden = false;
    this.searchTarget.focus();
  }

  close() {
    this.element.hidden = true;
    this.searchTarget.value = '';
    this.filter(); // reset list
  }

  /* ────────── click / follow link ────────── */

  async trigger(e) {
    const el = e.currentTarget;
    const href = el.href || el.dataset.href;
    const method = (el.dataset.turboMethod || el.dataset.method || 'get').toLowerCase();

    if (!href) {
      this.close();
      return;
    }

    // Check for Turbo confirmation
    const turboConfirm = el.dataset.turboConfirm;
    if (turboConfirm) {
      if (!(await confirmMessage(turboConfirm))) {
        this.close();
        return;
      }
    }

    if (method === 'get') {
      Turbo.visit(href);
    } else {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = href;

      if (method !== 'post') {
        const spoof = document.createElement('input');
        spoof.type = 'hidden';
        spoof.name = '_method';
        spoof.value = method;
        form.appendChild(spoof);
      }

      const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute('content');
      if (csrf) {
        const token = document.createElement('input');
        token.type = 'hidden';
        token.name = 'authenticity_token';
        token.value = csrf;
        form.appendChild(token);
      }

      document.body.appendChild(form);
      form.submit();
    }

    this.close();
  }

  /* ────────── live filter ────────── */

  filter() {
    const q = this.searchTarget.value.toLowerCase();
    this.itemTargets.forEach((el) => {
      const haystack =
        `${el.dataset.actionModalNameValue} ` +
        `${el.dataset.actionModalDescriptionValue} ` +
        `${el.dataset.actionModalTagsValue}`;
      el.classList.toggle('hidden', !haystack.includes(q));
    });
  }
}
